import { FC } from 'react';
import { Box, Link, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans } from '@libs/i18n';
import { Assessment } from '@libs/types';
import { ClockCounterClockwiseIcon } from '@phosphor-icons/react';

interface OldVersionBannerProps {
  activeVersion: Assessment;
  latestVersion: Assessment;
  onJumpToLatest: () => void;
}

const formatDate = (iso: string | null): string => {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso));
};

export const OldVersionBanner: FC<OldVersionBannerProps> = observer(({
  activeVersion,
  latestVersion,
  onJumpToLatest,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        backgroundColor: '#FAF5FD',
        border: `1px solid #E8D4F8`,
        color: theme.palette.violet[700],
        borderRadius: '12px',
        px: 2,
        py: 1.25,
      }}
    >
      <ClockCounterClockwiseIcon size={20} weight="bold" />
      <Typography variant="textSM_regular" sx={{ color: 'text.primary', flex: 1 }}>
        <Trans
          i18nKey="assessments.overview.versioning.oldVersionBanner.text"
          values={{
            activeVersion: `V${activeVersion.version}`,
            latestVersion: `V${latestVersion.version}`,
            latestDate: formatDate(latestVersion.submittedAt),
          }}
        />
      </Typography>
      <Link
        component="button"
        type="button"
        onClick={onJumpToLatest}
        sx={{
          color: theme.palette.violet[700],
          fontWeight: 600,
          fontSize: 13,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        <Trans i18nKey="assessments.overview.versioning.oldVersionBanner.jumpToLatest" /> →
      </Link>
    </Box>
  );
});
